/** What every paginated list endpoint in this platform answers. */
interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/**
 * 162 (US3, FR-008): read every page of a paginated endpoint, for a control that must offer every
 * option.
 *
 * 🔴 WHY A SINGLE REQUEST IS NOT ENOUGH. `GET /cars` defaults to 20 rows and caps at 100. Four
 * admin controls read it with no page parameter, so on a fleet over 20 cars they silently omit the
 * rest — the car is simply not in the dropdown, with no error and no note. Raising the ask to
 * `?limit=100` narrows that to a fleet of 100 without fixing it, and `?limit=200` is silently
 * REDUCED to 100 by `parsePageParams`, which is how `ReviewListPage` came to believe it had 200
 * cars while holding at most 100.
 *
 * A dropdown that quietly lacks the option you need is worse than a long dropdown, so this walks.
 */
const PAGE_SIZE = 100;

/**
 * A ceiling on the walk. `pages` is a number the platform computes, and a wrong one must not spin
 * forever inside an admin screen — a runaway loop in a dropdown is worse than a truncated one.
 * 50 pages of 100 is 5,000 rows, far beyond any option list this panel renders.
 */
const MAX_PAGES = 50;

export async function fetchAllPages<T>(
  path: string,
  get: (url: string) => Promise<PagedResponse<T>>,
): Promise<T[]> {
  const separator = path.includes('?') ? '&' : '?';
  const rows: T[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const answer = await get(`${path}${separator}page=${page}&limit=${PAGE_SIZE}`);
    rows.push(...answer.items);

    // Stop on an empty page whatever the count claims: a `pages` that disagrees with reality would
    // otherwise cost MAX_PAGES requests to discover.
    if (!answer.items.length) break;
    if (page >= (answer.pages ?? 1)) break;
  }

  return rows;
}
